'use client';

import { useState } from 'react';
import DOMPurify from 'dompurify';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import { FileCheck, Loader2 } from 'lucide-react';
import type { ContractTemplate } from '@shared/schema';

interface ContractAcceptanceModalProps {
  isOpen: boolean;
  onClose: () => void;
  template: ContractTemplate | null;
  onAccept: () => Promise<void>;
  title?: string;
  description?: string;
}

export default function ContractAcceptanceModal({
  isOpen,
  onClose,
  template,
  onAccept,
  title,
  description,
}: ContractAcceptanceModalProps) {
  const [isAccepting, setIsAccepting] = useState(false);

  const handleAccept = async () => {
    setIsAccepting(true);
    try {
      await onAccept();
    } finally {
      setIsAccepting(false);
    }
  };

  const handleOpenChange = (open: boolean) => {
    if (!open && !isAccepting) onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-2xl overflow-hidden">
        <DialogHeader className="pb-4 border-b border-border/50">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-xl bg-primary/10">
              <FileCheck className="h-5 w-5 text-primary" />
            </div>
            <div>
              <DialogTitle>
                {title ?? 'Conditions contractuelles'}
              </DialogTitle>
              {description && (
                <DialogDescription className="mt-0.5">{description}</DialogDescription>
              )}
            </div>
          </div>
        </DialogHeader>

        <ScrollArea className="max-h-[50vh] rounded-md border bg-muted/20 p-4">
          {template ? (
            <div
              className="prose prose-sm max-w-none text-sm leading-relaxed"
              dangerouslySetInnerHTML={{
                __html:
                  typeof window !== 'undefined'
                    ? DOMPurify.sanitize(template.content)
                    : '',
              }}
            />
          ) : (
            <p className="text-sm text-muted-foreground text-center py-8">
              Chargement des conditions...
            </p>
          )}
        </ScrollArea>

        <DialogFooter className="gap-2 pt-4 border-t border-border/50">
          <Button
            type="button"
            variant="outline"
            onClick={onClose}
            disabled={isAccepting}
            data-testid="button-contract-decline"
          >
            Refuser
          </Button>
          <Button
            type="button"
            onClick={handleAccept}
            disabled={isAccepting || !template}
            data-testid="button-contract-accept"
          >
            {isAccepting ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Enregistrement...
              </>
            ) : (
              "J'accepte"
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
